import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Divider,
  Skeleton,
  Typography,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LanguageIcon from '@mui/icons-material/Language';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import BusinessIcon from '@mui/icons-material/Business';
import { VerifiedBadge } from './index';
import { DESIGN_TOKENS } from '../../styles/theme';
import type { TransportCompany } from '../../types';
import { resolveAssetUrl } from '../../utils';
import { useI18n } from '../../i18n';

// ============================================================
// COMPANY CARD COMPONENT
// ============================================================

interface TransportCompanyCardProps {
  company: TransportCompany;
}

export function TransportCompanyCard({ company }: TransportCompanyCardProps) {
  const { t } = useI18n();
  const { company_information } = company;
  const { brand_graphics, company_data, contact_details, tax_information, verified, highlighted } =
    company_information;

  const logoUrl = resolveAssetUrl(brand_graphics?.logo?.url);
  const coverUrl = resolveAssetUrl(brand_graphics?.coverImage?.url);

  return (
    <Card
      component={RouterLink}
      to={`/companies/transport-companies/${company.documentId}`}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        textDecoration: 'none',
        overflow: 'hidden',
        cursor: 'pointer',
        '&:hover': {
          transform: 'translateY(-3px)',
          boxShadow: DESIGN_TOKENS.shadows.cardHover,
          textDecoration: 'none',
        },
      }}
    >
      {/* Cover image */}
      <Box
        sx={{
          height: 100,
          backgroundColor: DESIGN_TOKENS.colors.primaryLighter,
          backgroundImage: coverUrl ? `url(${coverUrl})` : undefined,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          position: 'relative',
          flexShrink: 0,
        }}
      >
        {/* Logo */}
        <Box
          sx={{
            position: 'absolute',
            bottom: -24,
            left: 20,
            width: 52,
            height: 52,
            borderRadius: '10px',
            border: `3px solid ${DESIGN_TOKENS.colors.surface}`,
            backgroundColor: DESIGN_TOKENS.colors.surface,
            overflow: 'hidden',
            boxShadow: DESIGN_TOKENS.shadows.card,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {logoUrl ? (
            <Box
              component="img"
              src={logoUrl}
              alt={`Logo ${company_data?.company_name}`}
              sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
          ) : (
            <BusinessIcon sx={{ color: DESIGN_TOKENS.colors.primaryLight, fontSize: 28 }} />
          )}
        </Box>
      </Box>

      <CardContent sx={{ pt: 4.5, pb: 2.5, px: 2.5, flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Name & badges */}
        <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5, lineHeight: 1.2 }}>
          {company_data?.company_name ?? t('companies.companyFallback')}
        </Typography>
        <Box sx={{ mb: 1.5 }}>
          <VerifiedBadge isVerified={verified ?? false} isHighlighted={highlighted ?? false} />
        </Box>

        {/* Location */}
        {contact_details?.location?.city && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
            <LocationOnIcon sx={{ fontSize: 14, color: DESIGN_TOKENS.colors.primary }} />
            <Typography variant="caption" color="text.secondary">
              {contact_details.location.city}
              {contact_details.location.country && `, ${contact_details.location.country}`}
            </Typography>
          </Box>
        )}

        {/* NIP */}
        {tax_information?.tax_identification_number && (
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>
            NIP: {tax_information.tax_identification_number}
          </Typography>
        )}

        {!verified && (
          <Typography variant="caption" sx={{ color: DESIGN_TOKENS.colors.error, fontWeight: 600, display: 'block', mb: 1.5 }}>{t('companies.unverifiedCompany')}</Typography>
        )}

        <Divider sx={{ my: 1.5, mt: 'auto' }} />

        {/* Contact details */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {contact_details?.email && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <EmailIcon sx={{ fontSize: 13, color: DESIGN_TOKENS.colors.primary }} />
              <Typography
                variant="caption"
                sx={{ color: DESIGN_TOKENS.colors.primary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              >
                {contact_details.email}
              </Typography>
            </Box>
          )}
          {contact_details?.phone_number && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <PhoneIcon sx={{ fontSize: 13, color: DESIGN_TOKENS.colors.primary }} />
              <Typography variant="caption" color="text.secondary">
                {contact_details.phone_number}
              </Typography>
            </Box>
          )}
          {contact_details?.website_url && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <LanguageIcon sx={{ fontSize: 13, color: DESIGN_TOKENS.colors.primary }} />
              <Typography
                variant="caption"
                sx={{ color: DESIGN_TOKENS.colors.primary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              >
                {contact_details.website_url.replace(/^https?:\/\//, '')}
              </Typography>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}

// ============================================================
// SKELETON CARD
// ============================================================

export function CompanyCardSkeleton() {
  return (
    <Card sx={{ height: '100%', overflow: 'hidden' }}>
      <Skeleton variant="rectangular" height={100} />
      <CardContent sx={{ pt: 4.5, px: 2.5 }}>
        <Skeleton variant="text" height={28} width="70%" sx={{ mb: 1 }} />
        <Skeleton variant="text" height={20} width="40%" sx={{ mb: 1.5 }} />
        <Skeleton variant="text" height={16} width="50%" sx={{ mb: 0.5 }} />
        <Skeleton variant="text" height={16} width="60%" sx={{ mb: 0.5 }} />
        <Skeleton variant="text" height={16} width="45%" />
      </CardContent>
    </Card>
  );
}
